import { StrictMode, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { setupGlobalErrorHandlers } from "./lib/globalErrorHandlers";

const getSentryEventMessage = (event: Sentry.Event): string => {
  const exceptionMessage = event.exception?.values
    ?.map((value) => value.value)
    .filter(Boolean)
    .join(" ");

  return [event.message, exceptionMessage].filter(Boolean).join(" ");
};

const hasFirstPartyFrame = (event: Sentry.Event): boolean =>
  event.exception?.values?.some((value) =>
    value.stacktrace?.frames?.some((frame) => {
      const filename = frame.filename ?? "";
      return (
        frame.in_app === true ||
        filename.includes("/src/") ||
        filename.includes("/assets/")
      );
    })
  ) ?? false;

Sentry.init({
  dsn: "https://70cce94713f0ce53ffab378fbe192182@o4509724470411264.ingest.de.sentry.io/4509726641619024",
  release: import.meta.env["VITE_SENTRY_RELEASE"],
  environment:
    import.meta.env["VITE_SENTRY_ENVIRONMENT"] ?? import.meta.env.MODE,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  beforeSend(event) {
    const message = getSentryEventMessage(event);
    const isRuntimeSendMessageNoise = message.includes(
      "Invalid call to runtime.sendMessage(). Tab not found."
    );

    if (isRuntimeSendMessageNoise && !hasFirstPartyFrame(event)) {
      return null;
    }

    return event;
  },
});

setupGlobalErrorHandlers();
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import OwnerProtectedRoute from "./components/OwnerProtectedRoute";
import SpecificRoleProtectedRoute from "./components/SpecificRoleProtectedRoute";
import { ContextProviderWrapper } from "./components/ContextProviderWrapper";
import {
  GlobalLoadingIndicator,
  useGlobalLoading,
} from "./components/GlobalLoadingIndicator";
import VersionChecker from "./components/VersionChecker";
import { createRoot } from "react-dom/client";
import "./index.css";
import theme from "./flowbite-theme";
import { initUpdateReloadSession } from "./utils/updateReloadDraft";
// import { Flowbite } from "flowbite-react";
import { ThemeProvider } from "flowbite-react";
import { Routes, Route } from "react-router";
import { BrowserRouter } from "react-router-dom";
import DashboardPage from "./pages";
import SignInPage from "./pages/authentication/sign-in";
import SignUpPage from "./pages/authentication/sign-up";
import VerifyPage from "./pages/auth/verify";
import SetPasswordPage from "./pages/auth/set-password";
import ContractTypesPage from "./pages/contract-types/ContractTypesPage";
import UserListPage from "./pages/users/list";
import LocationPage from "./pages/location";
import WorkerLocationsPage from "./pages/worker-locations";
import ContractCreationPage from "./pages/contract_creation";
import CustomerOverviewPage from "./pages/customers/overview";
import CustomerInformationPage from "./pages/customers/customer-information";
import AllCustomersPage from "./pages/customers/all-customers";
import CustomerHeatmapPage from "./pages/heatmap/CustomerHeatmapPage";
import TestMapboxPage from "./pages/test-mapbox";
import SalesMetricsPage from "./pages/sales-metrics";

import LocationPageVariant from "./pages/location_variant";
import ContractCreationPageVariant from "./pages/contract_creation_variant";
import ProfilePage from "./pages/profile/ProfilePage";
import PdfRegenerationPage from "./pages/admin/PdfRegenerationPage";
import EmailAuditPage from "./pages/admin/EmailAuditPage";
import ExportPage from "./pages/exports/ExportPage";
import { EXPORTS_ENABLED } from "./config/features";
// import BonusManagementPage from "./pages/admin/bonuses/BonusManagementPage";

const container = document.getElementById("root");

// Detect whether this boot follows a version-triggered reload.
initUpdateReloadSession();

if (!container) {
  throw new Error("React root element doesn't exist!");
}

const root = createRoot(container);

const App = () => {
  const isLoading = useGlobalLoading();

  // Initialize theme from localStorage on app load
  useEffect(() => {
    const savedTheme = localStorage.getItem("color-theme");
    if (
      savedTheme === "dark" ||
      (!savedTheme && window.matchMedia("(prefers-color-scheme: dark)").matches)
    ) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, []);

  return (
    <StrictMode>
      <ErrorBoundary>
        <ThemeProvider theme={theme}>
          <GlobalLoadingIndicator isLoading={isLoading} />
          <VersionChecker />
          <BrowserRouter>
            <ContextProviderWrapper>
              <Routes>
                {/* Public Routes */}
                <Route
                  path="/authentication/sign-in"
                  element={<SignInPage />}
                />
                <Route
                  path="/authentication/sign-up"
                  element={<SignUpPage />}
                />
                <Route path="/auth/verify" element={<VerifyPage />} />
                <Route
                  path="/auth/set-password"
                  element={<SetPasswordPage />}
                />

                {/* Protected Routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                  index
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contract-types"
                  element={
                    <AdminProtectedRoute>
                      <ContractTypesPage />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/users/list"
                  element={
                    <AdminProtectedRoute>
                      <UserListPage />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/location"
                  element={
                    <ProtectedRoute>
                      <LocationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/location_variant"
                  element={
                    <SpecificRoleProtectedRoute>
                      <LocationPageVariant />
                    </SpecificRoleProtectedRoute>
                  }
                />
                <Route
                  path="/contract-creation"
                  element={
                    <ProtectedRoute>
                      <ContractCreationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contract_creation_variant"
                  element={
                    <SpecificRoleProtectedRoute>
                      <ContractCreationPageVariant />
                    </SpecificRoleProtectedRoute>
                  }
                />
                <Route
                  path="/customers/overview"
                  element={
                    <ProtectedRoute>
                      <CustomerOverviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers/information/:secureToken"
                  element={
                    <ProtectedRoute>
                      <CustomerInformationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/test-mapbox"
                  element={
                    <ProtectedRoute>
                      <TestMapboxPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/worker-locations"
                  element={
                    <AdminProtectedRoute>
                      <WorkerLocationsPage />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/heatmap"
                  element={
                    <ProtectedRoute>
                      <CustomerHeatmapPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers/all-customers"
                  element={
                    <AdminProtectedRoute allowTeamLead>
                      <AllCustomersPage />
                    </AdminProtectedRoute>
                  }
                />
                {/* Team leads see their own teams' metrics; the RPCs scope rows
                    server-side via private.dashboard_data_worker_ids(), so this only
                    controls who can open the page. */}
                <Route
                  path="/sales-metrics"
                  element={
                    <AdminProtectedRoute allowTeamLead>
                      <SalesMetricsPage />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/pdf-regeneration"
                  element={
                    <AdminProtectedRoute>
                      <PdfRegenerationPage />
                    </AdminProtectedRoute>
                  }
                />
                {EXPORTS_ENABLED && (
                  <Route
                    path="/export"
                    element={
                      <AdminProtectedRoute>
                        <ExportPage />
                      </AdminProtectedRoute>
                    }
                  />
                )}
                <Route
                  path="/admin/email-audit"
                  element={
                    <OwnerProtectedRoute>
                      <EmailAuditPage />
                    </OwnerProtectedRoute>
                  }
                />
                {/*
                <Route
                  path="/admin/bonuses"
                  element={
                    <AdminProtectedRoute>
                      <BonusManagementPage />
                    </AdminProtectedRoute>
                  }
                />
                */}
              </Routes>
            </ContextProviderWrapper>
          </BrowserRouter>
        </ThemeProvider>
      </ErrorBoundary>
      <Analytics />
      <SpeedInsights />
    </StrictMode>
  );
};

root.render(<App />);
